import Decimal from 'decimal.js';

const LIMITS = {
  maxInputLength: 200,
  maxTokens: 200,
  maxDepth: 20,
  maxFunctionArgs: 10,
  maxExponentAbs: 1000,
  maxDecimalPlaces: 12,
};

Decimal.set({
  precision: 40,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -30,
  toExpPos: 30,
});

const CONSTANTS = {
  pi: new Decimal('3.141592653589793238462643383279502884197'),
  e: new Decimal('2.718281828459045235360287471352662497757'),
};

const FUNCTIONS = {
  sqrt: { minArgs: 1, maxArgs: 1, fn: ([value]) => value.sqrt() },
  abs: { minArgs: 1, maxArgs: 1, fn: ([value]) => value.abs() },
  round: {
    minArgs: 1,
    maxArgs: 1,
    fn: ([value]) => value.toDecimalPlaces(0, Decimal.ROUND_HALF_UP),
  },
  min: {
    minArgs: 1,
    maxArgs: LIMITS.maxFunctionArgs,
    fn: (values) => Decimal.min(...values),
  },
  max: {
    minArgs: 1,
    maxArgs: LIMITS.maxFunctionArgs,
    fn: (values) => Decimal.max(...values),
  },
};

const OPERATORS = {
  '+': { precedence: 1, assoc: 'L', arity: 2 },
  '-': { precedence: 1, assoc: 'L', arity: 2 },
  '*': { precedence: 2, assoc: 'L', arity: 2 },
  '/': { precedence: 2, assoc: 'L', arity: 2 },
  '^': { precedence: 4, assoc: 'R', arity: 2 },
  'u+': { precedence: 3, assoc: 'R', arity: 1 },
  'u-': { precedence: 3, assoc: 'R', arity: 1 },
  '%': { precedence: 5, assoc: 'L', arity: 1 },
};

const isDigit = (char) => char >= '0' && char <= '9';
const isAlpha = (char) => (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z');

const normalizeExpression = (rawInput) => {
  if (!rawInput || typeof rawInput !== 'string') {
    return '';
  }

  let value = rawInput.replace(/[\uFF01-\uFF5E]/g, (char) =>
    String.fromCharCode(char.charCodeAt(0) - 0xFEE0)
  );

  value = value
    .replace(/[\u2212\u2013\u2014\u2010\u2011\uFE63\uFF0D]/g, '-')
    .replace(/[×✕]/g, '*')
    .replace(/[÷]/g, '/')
    .replace(/[（）]/g, (char) => (char === '（' ? '(' : ')'))
    .replace(/[。]/g, '.')
    .replace(/[πΠ]/g, 'pi')
    .replace(/[\s\u3000]/g, '');

  value = value.replace(/(\d),(?=\d{3}(\D|$))/g, '$1');

  return value;
};

const tokenize = (input) => {
  const tokens = [];
  let index = 0;
  let depth = 0;

  while (index < input.length) {
    const char = input[index];

    if (isDigit(char) || char === '.') {
      let hasDot = char === '.';
      let end = index + 1;
      while (end < input.length) {
        const next = input[end];
        if (isDigit(next)) {
          end += 1;
        } else if (next === '.' && !hasDot) {
          hasDot = true;
          end += 1;
        } else {
          break;
        }
      }
      const numberValue = input.slice(index, end);
      if (numberValue === '.' || numberValue === '+.' || numberValue === '-.') {
        return null;
      }
      tokens.push({ type: 'number', value: numberValue });
      index = end;
      if (tokens.length > LIMITS.maxTokens) {
        return null;
      }
    } else if (isAlpha(char)) {
      let end = index + 1;
      while (end < input.length && (isAlpha(input[end]) || isDigit(input[end]))) {
        end += 1;
      }
      const rawId = input.slice(index, end).toLowerCase();
      if (FUNCTIONS[rawId]) {
        tokens.push({ type: 'function', value: rawId });
      } else if (CONSTANTS[rawId]) {
        tokens.push({ type: 'constant', value: rawId });
      } else {
        return null;
      }
      index = end;
      if (tokens.length > LIMITS.maxTokens) {
        return null;
      }
    } else if (char === '(') {
      depth += 1;
      if (depth > LIMITS.maxDepth) {
        return null;
      }
      tokens.push({ type: 'lparen', value: char });
      index += 1;
    } else if (char === ')') {
      depth -= 1;
      if (depth < 0) {
        return null;
      }
      tokens.push({ type: 'rparen', value: char });
      index += 1;
    } else if (char === ',') {
      tokens.push({ type: 'comma', value: char });
      index += 1;
    } else if (OPERATORS[char]) {
      tokens.push({ type: 'operator', value: char });
      index += 1;
    } else {
      return null;
    }
  }

  if (depth !== 0) {
    return null;
  }

  return tokens;
};

const isValueToken = (token) => {
  if (!token) {
    return false;
  }
  if (token.type === 'number' || token.type === 'constant' || token.type === 'rparen') {
    return true;
  }
  return token.type === 'operator' && token.value === '%';
};

const isValueStartToken = (token) => {
  if (!token) {
    return false;
  }
  return (
    token.type === 'number' ||
    token.type === 'constant' ||
    token.type === 'lparen' ||
    token.type === 'function'
  );
};

const insertImplicitMultiplication = (tokens) => {
  const result = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    const prev = result[result.length - 1];
    if (isValueToken(prev) && isValueStartToken(token)) {
      result.push({ type: 'operator', value: '*' });
    }
    result.push(token);
  }
  return result;
};

const applyUnaryOperators = (tokens) => {
  const result = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const token = { ...tokens[i] };
    const prev = result[result.length - 1];

    if (token.type === 'operator' && (token.value === '+' || token.value === '-')) {
      if (!prev || prev.type === 'operator' || prev.type === 'lparen' || prev.type === 'comma') {
        token.value = token.value === '+' ? 'u+' : 'u-';
      }
    }

    if (token.type === 'operator' && token.value === '%') {
      if (!isValueToken(prev)) {
        return null;
      }
    }

    result.push(token);
  }
  return result;
};

const toRpn = (tokens) => {
  const output = [];
  const operatorStack = [];
  const argStack = [];
  let prevToken = null;

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];

    if (token.type === 'number' || token.type === 'constant') {
      output.push(token);
      prevToken = token;
    } else if (token.type === 'function') {
      operatorStack.push(token);
      argStack.push({ count: 0 });
      prevToken = token;
    } else if (token.type === 'comma') {
      if (!prevToken || prevToken.type === 'lparen' || prevToken.type === 'comma') {
        return null;
      }
      while (operatorStack.length && operatorStack[operatorStack.length - 1].type !== 'lparen') {
        output.push(operatorStack.pop());
      }
      if (!operatorStack.length || !argStack.length) {
        return null;
      }
      argStack[argStack.length - 1].count += 1;
      prevToken = token;
    } else if (token.type === 'operator') {
      const current = OPERATORS[token.value];
      if (!current) {
        return null;
      }
      while (operatorStack.length) {
        const top = operatorStack[operatorStack.length - 1];
        if (top.type !== 'operator') {
          break;
        }
        const topInfo = OPERATORS[top.value];
        if (
          (current.assoc === 'L' && current.precedence <= topInfo.precedence) ||
          (current.assoc === 'R' && current.precedence < topInfo.precedence)
        ) {
          output.push(operatorStack.pop());
        } else {
          break;
        }
      }
      operatorStack.push(token);
      prevToken = token;
    } else if (token.type === 'lparen') {
      operatorStack.push(token);
      prevToken = token;
    } else if (token.type === 'rparen') {
      if (!prevToken || prevToken.type === 'comma') {
        return null;
      }
      while (operatorStack.length && operatorStack[operatorStack.length - 1].type !== 'lparen') {
        output.push(operatorStack.pop());
      }
      if (!operatorStack.length) {
        return null;
      }
      operatorStack.pop();
      if (operatorStack.length && operatorStack[operatorStack.length - 1].type === 'function') {
        const fnToken = operatorStack.pop();
        const args = argStack.pop();
        if (!args || prevToken.type === 'lparen') {
          return null;
        }
        fnToken.argCount = args.count + 1;
        output.push(fnToken);
      }
      prevToken = token;
    } else {
      return null;
    }
  }

  while (operatorStack.length) {
    const op = operatorStack.pop();
    if (op.type === 'lparen' || op.type === 'rparen') {
      return null;
    }
    output.push(op);
  }

  if (argStack.length) {
    return null;
  }

  return output;
};

const evaluateRpn = (tokens) => {
  const stack = [];

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token.type === 'number') {
      if (token.value.length > LIMITS.maxInputLength) {
        return null;
      }
      stack.push(new Decimal(token.value));
    } else if (token.type === 'constant') {
      stack.push(CONSTANTS[token.value]);
    } else if (token.type === 'operator') {
      const info = OPERATORS[token.value];
      if (!info) {
        return null;
      }
      if (info.arity === 1) {
        if (stack.length < 1) {
          return null;
        }
        const value = stack.pop();
        if (token.value === 'u+') {
          stack.push(value);
        } else if (token.value === 'u-') {
          stack.push(value.neg());
        } else if (token.value === '%') {
          stack.push(value.div(100));
        } else {
          return null;
        }
      } else {
        if (stack.length < 2) {
          return null;
        }
        const right = stack.pop();
        const left = stack.pop();
        if (token.value === '+') {
          stack.push(left.plus(right));
        } else if (token.value === '-') {
          stack.push(left.minus(right));
        } else if (token.value === '*') {
          stack.push(left.times(right));
        } else if (token.value === '/') {
          if (right.isZero()) {
            return null;
          }
          stack.push(left.div(right));
        } else if (token.value === '^') {
          if (!right.isFinite() || right.abs().greaterThan(LIMITS.maxExponentAbs)) {
            return null;
          }
          if (!right.isInteger() && left.isNegative()) {
            return null;
          }
          stack.push(left.pow(right));
        } else {
          return null;
        }
      }
    } else if (token.type === 'function') {
      const fn = FUNCTIONS[token.value];
      if (!fn) {
        return null;
      }
      const argCount = token.argCount || 0;
      if (argCount < fn.minArgs || argCount > fn.maxArgs || argCount > LIMITS.maxFunctionArgs) {
        return null;
      }
      if (stack.length < argCount) {
        return null;
      }
      const args = stack.splice(stack.length - argCount, argCount);
      try {
        stack.push(fn.fn(args));
      } catch (error) {
        return null;
      }
    } else {
      return null;
    }
  }

  if (stack.length !== 1) {
    return null;
  }

  const result = stack[0];
  if (!result || !result.isFinite()) {
    return null;
  }
  return result;
};

const formatDecimal = (value) => {
  if (!value || !value.isFinite()) {
    return null;
  }
  const normalized = value.isZero() ? new Decimal(0) : value;
  const decimalPlaces = normalized.isInteger()
    ? 0
    : Math.min(normalized.decimalPlaces(), 2);
  let text = normalized.toFixed(decimalPlaces);
  if (text.includes('.')) {
    text = text.replace(/\.?0+$/, '');
  }
  const sign = text.startsWith('-') ? '-' : '';
  const [intPart, fracPart] = (sign ? text.slice(1) : text).split('.');
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${withCommas}${fracPart ? `.${fracPart}` : ''}`;
};

export const getMathExpressionPreview = (rawInput) => {
  const normalized = normalizeExpression(rawInput);
  if (!normalized || normalized.length > LIMITS.maxInputLength) {
    return null;
  }

  const tokens = tokenize(normalized);
  if (!tokens || tokens.length > LIMITS.maxTokens) {
    return null;
  }

  const withImplicit = insertImplicitMultiplication(tokens);
  const withUnary = applyUnaryOperators(withImplicit);
  if (!withUnary) {
    return null;
  }

  const rpn = toRpn(withUnary);
  if (!rpn || rpn.length > LIMITS.maxTokens) {
    return null;
  }

  const value = evaluateRpn(rpn);
  if (!value) {
    return null;
  }

  return formatDecimal(value);
};

export default getMathExpressionPreview;
